import { message, Modal, Skeleton, Input } from "antd";
import React, { useEffect, useRef, useState } from "react";
import WebUtils from "../../../../utils/WebUtils";
import {
  getFreeSingleJobSpecificQuestionApi,
  getJobSpecificIncrementCountApi,
  getSingleJobSpecificQuestionApi,
  postJobSpecificSubmitAnswerApi,
} from "../../../../api/candidate";
import { ChevronDown, ChevronRight, Keyboard, Mic } from "lucide-react";
import HalfCircleProgress from "../HalfCircleProgress";
import PdfDownload from "../PdfDownload";
import Loader from "../../../Loader";

const { TextArea } = Input;

// Function to get difficulty color
const getDifficultyColor = (difficulty) => {
  switch (difficulty.toLowerCase()) {
    case "easy":
      return "bg-green-100 text-green-800";
    case "medium":
      return "bg-yellow-100 text-yellow-800";
    case "hard":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

function PracticeJobSpecificModal({
  question,
  openJobSpecificModal,
  setOpenJobSpecificModal,
  guideId,
  userDetail,
  industryId,
  setJobSpecificQuestions,
  selectedQuestion,
  setModuleInfo,
  questionBank,
}) {
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const [questionData, setQuestionData] = useState(null);
  const [showAnswerArea, setShowAnswerArea] = useState(false);
  const [answer, setAnswer] = useState("");
  const [showHint, setShowHint] = useState(false);
  const [expandedSample, setExpandedSample] = useState([]);
  const [aiSuggestionFlag, setAiSuggestionFlag] = useState(false);
  const [aiSuggestionData, setAiSuggestionData] = useState(null);
  const [loading, setLoading] = useState(null);

  const getQuestionData = async () => {
    let url;
    if (industryId) {
      url = `${getFreeSingleJobSpecificQuestionApi}?jobGuideIdentifier=${guideId}&questionId=${question?.questionId}`;
    } else {
      url = `${getSingleJobSpecificQuestionApi}?jobGuideIdentifier=${guideId}&userIdentifier=${
        userDetail?.userIdentifier || null
      }&questionId=${question?.questionId}`;
    }

    setSkeletonLoading(true);

    WebUtils.httpOperations(url, {}, "GET")
      .then((result) => {
        setSkeletonLoading(false);
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          setQuestionData(result.data?.data);
        }
      })
      .catch((err) => {
        setSkeletonLoading(false);
        console.log(err);
      });
  };
  const getIncrement = async () => {
    let url;

    url = `${getJobSpecificIncrementCountApi}?questionId=${question?.questionId}`;

    setLoading(true);

    WebUtils.httpOperations(url, {}, "POST")
      .then((result) => {
        setLoading(false);
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          if (questionBank) {
            return;
          } else {
            setJobSpecificQuestions((prev) => ({
              ...prev,
              specificQuestionResponseDataList:
                prev.specificQuestionResponseDataList?.map((q) =>
                  q.questionId === selectedQuestion?.questionId
                    ? { ...q, practiceCount: (q.practiceCount || 0) + 1 }
                    : q
                ),
            }));
          }
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };
  const postAnswer = async (ans) => {
    let url;

    url = `${postJobSpecificSubmitAnswerApi}`;

    setLoading(true);
    let body = {
      userIdentifier: userDetail?.userIdentifier,
      jobGuideIdentifier: guideId,
      answer: ans,
      skillName: selectedQuestion?.skillName,
      subSkillName: selectedQuestion?.subSkillName,
      skillType: selectedQuestion?.skillType,
      questionTitle: selectedQuestion?.questionTitle,
      difficulty: selectedQuestion?.difficulty,
      freeGuide: industryId ? true : false,
    };

    WebUtils.httpOperations(url, body, "POST")
      .then((result) => {
        setLoading(false);
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          setShowHint(false);
          setAiSuggestionFlag(true);
          message.success("Answer submitted successfully!");
          setAiSuggestionData(result.data.data);
          getIncrement();
          if (!questionBank) {
            setModuleInfo((prev) => ({
              ...prev,
              answerSubmissionCredits:
                prev?.answerSubmissionCredits > 0
                  ? prev.answerSubmissionCredits - 1
                  : 0, // keep it at 0
            }));
          }
        } else if (
          result.data.resultStatusInfo.resultCode === "INSUFFICIENT_CREDITS"
        ) {
          message.error("Answer submission failed. Insufficient credit!");
        } else {
          message.error("Answer submission failed!");
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };
  useEffect(() => {
    getQuestionData();
  }, []);

  const handleStartTyping = () => {
    setShowAnswerArea(true);
  };

  const handleSubmitAnswer = () => {
    if (answer.trim()) {
      postAnswer(answer);
    }
  };

  const handleHint = () => {
    setShowHint((prev) => !prev);
  };
  const toggleSampleAnswer = (index) => {
    setExpandedSample((prev) => ({
      ...prev,
      [index]: !prev[index], // toggle true/false
    }));
  };

  const printReport = () => {
    const WinPrint = window.open("", "", "width=900,height=650");

    WinPrint.document.write(`
  <html>
    <head>
      <title>Print Answer</title>
      <style>
        body { font-family: Inter, sans-serif; padding: 20px; }
        h2, h3 { margin: 0 0 10px 0; }
        p { margin: 5px 0; }
        ul { margin: 5px 0 15px 20px; padding: 0; }
      </style>
    </head>
    <body>
      <div style="padding:15px; font-family: Inter, sans-serif;">
        <!-- Header Section -->
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom:30px">
       <svg
          width=60
          height=60
          viewBox="0 0 48 42"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.54984 17.8658L6.70368 16.0724L3.11685 15.7648L2.96301 17.5582L6.54984 17.8658ZM8.5612 23.9832L9.14365 22.28L8.5612 23.9832ZM12.7563 23.712L11.9965 22.0803L12.7563 23.712ZM17.5563 18.0006C17.5567 17.0065 16.7511 16.2003 15.757 16.2C14.7629 16.1996 13.9567 17.0052 13.9563 17.9994L17.5563 18.0006ZM5.5313 21.3928L7.07726 20.4708L5.5313 21.3928ZM8.5612 23.9832L7.97876 25.6863C9.89033 26.34 11.7313 26.175 13.5162 25.3438L12.7563 23.712L11.9965 22.0803C10.9811 22.5531 10.1019 22.6077 9.14365 22.28L8.5612 23.9832ZM12.7563 23.712L13.5162 25.3438C14.8373 24.7286 15.8333 23.5355 16.4833 22.2926C17.1469 21.0238 17.5558 19.5078 17.5563 18.0006L15.7563 18L13.9563 17.9994C13.956 18.8837 13.708 19.8312 13.2932 20.6242C12.865 21.4431 12.3614 21.9103 11.9965 22.0803L12.7563 23.712ZM4.75643 17.712C2.96301 17.5582 2.96297 17.5586 2.96293 17.5591C2.96292 17.5593 2.96288 17.5598 2.96285 17.5601C2.96279 17.5608 2.96273 17.5615 2.96267 17.5622C2.96255 17.5637 2.96241 17.5653 2.96227 17.567C2.96199 17.5705 2.96167 17.5745 2.96132 17.5791C2.96062 17.5881 2.95979 17.5992 2.95889 17.6123C2.95709 17.6385 2.95498 17.6727 2.95289 17.7141C2.94872 17.797 2.94464 17.9098 2.94348 18.0472C2.94119 18.3206 2.95044 18.699 2.99587 19.1381C3.0839 19.9888 3.32067 21.2002 3.98535 22.3147L5.5313 21.3928L7.07726 20.4708C6.78847 19.9866 6.6378 19.3576 6.57676 18.7676C6.54766 18.4863 6.54196 18.2444 6.54336 18.0774C6.54405 17.9946 6.54647 17.9322 6.54834 17.895C6.54928 17.8765 6.55006 17.8643 6.55042 17.8592C6.55059 17.8567 6.55066 17.8559 6.55057 17.857C6.55053 17.8575 6.55045 17.8585 6.55033 17.86C6.55027 17.8607 6.5502 17.8616 6.55012 17.8626C6.55008 17.863 6.55004 17.8636 6.54999 17.8641C6.54997 17.8644 6.54993 17.8648 6.54992 17.865C6.54988 17.8654 6.54984 17.8658 4.75643 17.712ZM5.5313 21.3928L3.98535 22.3147C5.01065 24.0339 6.24056 25.0919 7.97876 25.6863L8.5612 23.9832L9.14365 22.28C8.31503 21.9966 7.72445 21.556 7.07726 20.4708L5.5313 21.3928Z"
            fill="#EDC043"
          />
          <path
            d="M30.7568 21L30.7553 20.7841M30.7568 21C30.7568 24.9782 29.1765 28.7936 26.3634 31.6066C23.5504 34.4196 19.7351 36 15.7568 36V29.5M30.7568 21C30.7568 17.0218 29.1765 13.2064 26.3634 10.3934C23.5504 7.58035 19.7351 6 15.7568 6V18"
            stroke="#EDC043"
            stroke-width="3.6"
          />
          <path
            d="M31.7568 20C30.9284 20 30.2568 20.6716 30.2568 21.5C30.2568 22.3284 30.9284 23 31.7568 23V20ZM43.2568 21.5L44.4568 22.4L45.1318 21.5L44.4568 20.6L43.2568 21.5ZM39.9568 14.6L39.0568 13.4L36.6568 15.2L37.5568 16.4L39.9568 14.6ZM37.5568 26.6L36.6568 27.8L39.0568 29.6L39.9568 28.4L37.5568 26.6ZM31.7568 21.5V23H43.2568V21.5V20H31.7568V21.5ZM43.2568 21.5L44.4568 20.6L39.9568 14.6L38.7568 15.5L37.5568 16.4L42.0568 22.4L43.2568 21.5ZM43.2568 21.5L42.0568 20.6L37.5568 26.6L38.7568 27.5L39.9568 28.4L44.4568 22.4L43.2568 21.5Z"
            fill="#EDC043"
          />
        </svg>
          <span style="font-size: 28px; font-weight: bold; color: #edc043;">
           JobDrona
          </span>
        </div>
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:10px;">
          <div>
            ${questionData.skillName} | ${questionData.subSkillName}
          </div>
          <span style="
            background: ${
              questionData.difficulty === "High"
                ? "#ffcccc"
                : questionData.difficulty === "Medium"
                ? "lightgreen"
                : "#f0f0f0"
            };
            padding:4px 8px;
            border-radius:6px;">
            ${questionData.difficulty}
          </span>
        </div>

        <!-- Question Section -->
        <div style="margin:20px 0;">
          <h2>${questionData.questionTitle}</h2>
        </div>

        <div style="margin:20px 0;">
          <p><strong>Sample Answer</strong></p>
          <div  style="border:1px solid gray; border-radius:6px; padding:10px;">
          <ul>
            ${
              questionData.sampleAnswerList
                ?.map((s) => `<li style="margin-bottom:4px">${s}</li>`)
                .join("") || ""
            }
          </ul>
          </div> 
        </div>

        <!-- User Answer Section -->
        <div style="margin:30px 0;">
          <h3>Your Answer</h3>
          <p style="border:1px solid gray; border-radius:6px; padding:10px;">
            ${answer}
          </p>
        </div>

        <!-- AI Section -->
        <h3>AI Suggestions</h3>
        <div style="border:1px solid gray; border-radius:6px; padding:16px; margin:20px 0;">
          <div>
            <p><strong>Overall Feedback</strong></p>
            <p>${aiSuggestionData?.overallFeedback || ""}</p>
          </div>

          <div style="margin:20px 0;">
            <p><strong>Overall Rating</strong></p>
            <p>${aiSuggestionData?.overallRating || ""} / 10</p>
          </div>

          <div style="margin:20px 0;">
            <p><strong>Strengths</strong></p>
            <ul>
              ${
                aiSuggestionData?.keyStrengths
                  ?.map((s) => `<li style="margin-bottom:4px">${s}</li>`)
                  .join("") || ""
              }
            </ul>
          </div>

          <div style="margin:20px 0;">
            <p><strong>Weakness</strong></p>
            <ul>
              ${
                aiSuggestionData?.weakness
                  ?.map((i) => `<li style="margin-bottom:4px">${i}</li>`)
                  .join("") || ""
              }
            </ul>
          </div>

          <div style="margin:20px 0;">
            <p><strong>Recommended Action</strong></p>
              <ul>
              ${
                aiSuggestionData?.recommendedActions
                  ?.map((s) => `<li style="margin-bottom:4px">${s}</li>`)
                  .join("") || ""
              }
            </ul>
          </div>

          <div style="margin:20px 0;">
            <p><strong>Ideal Answer</strong></p>
            <p>${aiSuggestionData?.idealAnswer || ""}</p>
          </div>
        </div>

        <div style="margin:30px 0; text-align: center;">
          <p>&copy; ${new Date().getFullYear()} JobDrona Technogies Private Limited. All rights reserved.</p>
        </div>
      </div>
    </body>
  </html>
`);

    WinPrint.document.close();
    WinPrint.focus();
    WinPrint.print();
  };

  return (
    <>
      {loading && <Loader />}
      <Modal
        open={openJobSpecificModal}
        onCancel={() => setOpenJobSpecificModal(false)}
        centered
        footer={null}
        width={1100}
      >
        {skeletonLoading ? (
          <>
            <Skeleton className="py-2" />
          </>
        ) : (
          <div className="pt-2 pb-1">
            {/* Header Section */}
            {/* Meta Information */}
            <div className="mb-1 flex flex-wrap justify-between items-center gap-3 pr-6">
              <div className="text-[.94rem] flex items-center flex-wrap gap-2">
                <span className="text-gray-500 gap-y-1 font-medium text-sm flex sm:flex-row flex-col sm:items-center items-start gap-x-1">
                  <span>{questionData?.skillName}</span>
                  <span className="border-l sm:block hidden border-gray-500 h-4 mx-2"></span>
                  <span>{questionData?.subSkillName}</span>
                </span>
              </div>

              <span className="text-sm text-gray-600">
                {/* {questionData?.creationData &&
              getHoursAgo(questionData?.creationData)} */}
                {questionData?.difficulty && (
                  <span
                    className={`px-3 py-[.25rem] border border-gray-200 text-sm font-medium rounded-lg ${getDifficultyColor(
                      questionData?.difficulty
                    )}`}
                  >
                    {questionData?.difficulty
                      ? questionData.difficulty.charAt(0).toUpperCase() +
                        questionData.difficulty.slice(1)
                      : ""}
                  </span>
                )}
              </span>
            </div>

            <div className="mb-[1.1rem] mt-3">
              <div className="text-xl leading-[1.6rem] font-semibold text-[#101828]">
                {questionData?.questionTitle}
              </div>
              <div className="flex gap-2 items-center mt-2 flex-wrap">
                {questionData?.tags?.map((tag, i) => {
                  return (
                    <span className="border-2 border-[#FEDF89] bg-[#FFFAEB] text-[#B54708] rounded-full px-3 py-[.1rem] text-[.75rem]">
                      {tag}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="max-h-[68vh] overflow-y-auto scroll px-2">
              {/* Explanation Section (only shows when Hint is active) */}
              {/* bg-[#FFFAEB] */}
              {showHint && (
                <div className=" mb-4">
                  <div className=" bg-[#0F1B23] px-6 py-[1.1rem]  rounded-lg  pr-3 mb-4">
                    {/* Question Explanation */}
                    <div className="mb-5">
                      <div className="text-[1.08rem]  font-semibold text-[#EDC043] mb-2">
                        Question Explanation
                      </div>
                      <p className="text-[#D0D5DD] rounded-lg px-6 py-5 leading-relaxed bg-[#1D2939]">
                        {questionData?.explanation
                          ? questionData?.explanation
                          : "No Question explanation found!"}
                      </p>
                    </div>

                    {/* Approach to Answer */}
                    {questionData?.approachToAnswer && (
                      <div className="mb-5 ">
                        <div className="text-[1.08rem] font-semibold text-[#EDC043] mb-2">
                          Approach to Answer
                        </div>
                        <p className="text-[#D0D5DD] rounded-lg px-6 py-5 leading-relaxed bg-[#1D2939]  ">
                          {questionData?.approachToAnswer
                            ? questionData?.approachToAnswer
                            : "No Answer Approach found!"}
                        </p>
                      </div>
                    )}
                  </div>
                  {/* Sample Answers */}
                  <div className="bg-gray-200 px-6 py-[1.1rem] border-gray-100 rounded-lg ">
                    {questionData?.sampleAnswerList &&
                    questionData.sampleAnswerList.length > 0 ? (
                      <div>
                        <div className="text-[1.08rem] font-semibold text-black mb-2">
                          Sample Answers
                        </div>
                        <div className="space-y-2">
                          {questionData.sampleAnswerList.map(
                            (sampleAnswer, index) => (
                              <div
                                key={index}
                                className="border border-gray-200 rounded-lg bg-white"
                              >
                                <button
                                  onClick={() => toggleSampleAnswer(index)}
                                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                                >
                                  <span className="font-semibold text-gray-800">
                                    Sample Answer {index + 1}
                                  </span>
                                  {expandedSample[index] === true ? (
                                    <ChevronDown
                                      size={20}
                                      className="text-gray-500"
                                    />
                                  ) : (
                                    <ChevronRight
                                      size={20}
                                      className="text-gray-500"
                                    />
                                  )}
                                </button>

                                {expandedSample[index] === true && (
                                  <div className="px-4 pb-3  border-gray-200">
                                    <div className="">
                                      <p className="text-gray-700 font-medium leading-relaxed text-sm">
                                        {sampleAnswer}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    ) : (
                      <div>No Sample Answer found!</div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Stats */}
              {/* <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-200">
          <div className="flex gap-6 text-sm text-gray-600">
            <span>Views: {questionData?.viewCount}</span>
            <span>Practice Count: {questionData?.practiceCount}</span>
          </div>
        </div> */}

              {/* Action Buttons */}
              <div className="mt-[1.2rem] mb-1 flex flex-wrap gap-2 justify-between">
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    disabled
                    className="px-[.85rem] py-[.43rem] flex gap-[.55rem] justify-center items-center btn-disabled cursor-not-allowed"
                  >
                    <Mic size={19} color="currentColor" />
                    Start Speaking
                  </button>

                  <button
                    onClick={handleStartTyping}
                    disabled={showAnswerArea}
                    className=" flex gap-[.55rem] justify-center items-center px-[.85rem] py-[.43rem] btn-white disabled:hover:brightness-100 disabled:cursor-not-allowed transition-colors"
                  >
                    <Keyboard size={19} color="currentColor" />{" "}
                    {showAnswerArea ? "Typing Mode..." : "Type your answer"}
                  </button>
                </div>

                <button
                  onClick={handleHint}
                  className="flex text-[.9rem] font-semibold gap-[.4rem] justify-center items-center px-3 py-[.4rem] text-[#ED7422] hover:brightness-125 transition-all duration-200"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clip-path="url(#clip0_853_6061)">
                      <path
                        d="M8.33268 14.7148V16.666C8.33268 17.5865 9.07887 18.3327 9.99935 18.3327C10.9198 18.3327 11.666 17.5865 11.666 16.666V14.7148M9.99935 1.66602V2.49935M2.49935 9.99935H1.66602M4.58268 4.58268L4.0826 4.0826M15.416 4.58268L15.9162 4.0826M18.3327 9.99935H17.4993M14.9993 9.99935C14.9993 12.7608 12.7608 14.9993 9.99935 14.9993C7.23793 14.9993 4.99935 12.7608 4.99935 9.99935C4.99935 7.23793 7.23793 4.99935 9.99935 4.99935C12.7608 4.99935 14.9993 7.23793 14.9993 9.99935Z"
                        stroke="currentColor"
                        stroke-width="1.67"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_853_6061">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span className="mt-[.15rem]">
                    {showHint ? "Hide Hint" : "Show Hint"}
                  </span>
                </button>
              </div>

              {/* Answer Area (conditionally rendered) */}
              {showAnswerArea && (
                <div className="mt-3">
                  <TextArea
                    showCount
                    maxLength={1200}
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Type your answer here..."
                    style={{ height: 128, resize: "none" }}
                    className="focus:outline-none focus:ring-1 focus:ring-orange-300/80 focus:border-transparent"
                  />

                  {aiSuggestionFlag && (
                    <div className="mt-3 text-[#344054] ">
                      <div className="font-semibold text-lg mb-2">
                        AI Suggestions
                      </div>
                      <div className="p-4 bg-[#EAECF0]  rounded-lg">
                        <div className="flex sm:flex-row flex-col gap-4">
                          <div className="rounded-lg bg-white p-2 sm:flex-1 w-full">
                            <p className="text-sm font-semibold">
                              Overall Feedback
                            </p>
                            <p className="text-[#475467] mt-2 ">
                              {aiSuggestionData?.overallFeedback}
                            </p>
                          </div>
                          <div className="rounded-lg bg-white p-2 sm:w-[240px] w-full">
                            <p className="text-sm font-semibold mb-2">
                              Overall score
                            </p>
                            <div className=" flex justify-center items-center h-[80%]">
                              <HalfCircleProgress
                                value={aiSuggestionData?.overallRating}
                                total={10}
                                sizeOfProgress={150}
                                height={90}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex md:flex-row flex-col gap-4 mt-4">
                          <div className="rounded-lg bg-white p-2 md:w-1/2 w-full">
                            <p className="text-sm font-semibold">
                              Key strengths
                            </p>
                            <ul
                              className="mt-2"
                              style={{
                                listStyleType: "disc",
                                paddingLeft: "20px",
                              }}
                            >
                              {aiSuggestionData?.keyStrengths?.map(
                                (point, index) => (
                                  <li
                                    key={index}
                                    style={{ marginBottom: "6px" }}
                                  >
                                    {point}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                          <div className="rounded-lg bg-white p-2 md:w-1/2 w-full">
                            <p className="text-sm font-semibold">Weaknesses</p>
                            <ul
                              className="mt-2"
                              style={{
                                listStyleType: "disc",
                                paddingLeft: "20px",
                              }}
                            >
                              {aiSuggestionData?.weakness?.map(
                                (point, index) => (
                                  <li
                                    key={index}
                                    style={{ marginBottom: "6px" }}
                                  >
                                    {point}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        </div>
                        <div className="mt-4">
                          <div className="rounded-lg bg-white p-2 w-full">
                            <p className="text-sm font-semibold">
                              Recommended Actions
                            </p>
                            <ul
                              className="mt-2"
                              style={{
                                listStyleType: "disc",
                                paddingLeft: "20px",
                              }}
                            >
                              {aiSuggestionData?.recommendedActions?.map(
                                (point, index) => (
                                  <li
                                    key={index}
                                    style={{ marginBottom: "6px" }}
                                  >
                                    {point}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        </div>
                        <div className="mt-4 rounded-lg bg-white p-2 sm:flex-1 w-full">
                          <p className="text-sm font-semibold">Ideal Answer</p>
                          <p className="text-[#475467] mt-2 ">
                            {aiSuggestionData?.idealAnswer}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end mt-5 gap-x-4">
                    {aiSuggestionFlag && (
                      <button
                        onClick={printReport}
                        // onClick={() =>
                        //   PdfDownload(questionData, answer, aiSuggestionData)
                        // }
                        className="btn-white px-6 py-2"
                      >
                        Download Answer
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (aiSuggestionFlag) {
                          setAnswer("");
                          setAiSuggestionFlag(false);
                        } else {
                          handleSubmitAnswer();
                        }
                      }}
                      disabled={!answer.trim()}
                      className={`${
                        !answer.trim()
                          ? "btn-disabled cursor-not-allowed"
                          : "btn-orange cursor-pointer"
                      } px-6 py-2  transition-colors`}
                    >
                      {aiSuggestionFlag
                        ? "Retry Answer"
                        : "Submit & Get AI Feedback"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

export default PracticeJobSpecificModal;
